import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fetchMensagensChamado } from "@/services/chamados";
import { getAttachments } from "@/services/clientAreaService";
import { AnexoChamado, SacChamado, SacMensagem } from "@/types/Chamados";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowLeft, Check, FileText, Loader2, User } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

interface ChamadoHistoricoDetailProps {
  chamado: SacChamado;
  onBack: () => void;
}

export default function ChamadoHistoricoDetail({
  chamado,
  onBack,
}: ChamadoHistoricoDetailProps) {
  const [mensagens, setMensagens] = useState<SacMensagem[]>([]);
  const [anexosChamado, setAnexosChamado] = useState<AnexoChamado[]>([]);
  const [anexosMensagens, setAnexosMensagens] = useState<
    Record<string, AnexoChamado[]>
  >({});
  const [loading, setLoading] = useState(true);

  const loadMensagens = useCallback(async () => {
    try {
      setLoading(true);

      const data = await fetchMensagensChamado(chamado.sac_codigo);
      setMensagens(data);

      try {
        const anexosChamadoData = await getAttachments(
          "sac",
          chamado.sac_codigo,
        );
        setAnexosChamado(anexosChamadoData);
      } catch (error) {
        console.log("Sem anexos no chamado ou erro ao carregar:", error);
        setAnexosChamado([]);
      }

      const anexosPromises = data.map(async (msg) => {
        try {
          const anexos = await getAttachments("smg", msg.smg_codigo);
          return { smg_codigo: msg.smg_codigo, anexos };
        } catch {
          return { smg_codigo: msg.smg_codigo, anexos: [] };
        }
      });

      const anexosResults = await Promise.all(anexosPromises);
      const anexosMap: Record<string, AnexoChamado[]> = {};
      anexosResults.forEach((result) => {
        anexosMap[result.smg_codigo] = result.anexos;
      });
      setAnexosMensagens(anexosMap);
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
    } finally {
      setLoading(false);
    }
  }, [chamado.sac_codigo]);

  useEffect(() => {
    loadMensagens();
  }, [loadMensagens]);

  const getFileName = (filePath: string) => {
    return filePath.split("/").pop() || filePath;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-n-green hover:bg-n-green/10"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para lista
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-n-green">Detalhes do Chamado</CardTitle>
            <span className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium bg-green-100 text-green-700 border-green-200">
              Concluído
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Protocolo</p>
              <p className="font-semibold text-gray-800">
                #{chamado.sac_codigo}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Data de Abertura</p>
              <p className="font-semibold text-gray-800">
                {format(
                  new Date(chamado.sac_data_abertura),
                  "dd/MM/yyyy 'às' HH:mm",
                  { locale: ptBR },
                )}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Cotação</p>
              <p className="font-semibold text-gray-800">
                #{chamado.cve_codigo}
              </p>
            </div>
          </div>

          <div className="pt-4 border-t">
            <p className="text-xs text-gray-500 mb-2">Descrição do Problema</p>
            <p className="text-sm text-gray-700">{chamado.sac_descricao}</p>
          </div>

          {chamado.sac_parecer_final && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-gray-500 mb-2">Parecer Final</p>
              <p className="text-sm text-gray-700 bg-green-50 p-3 rounded-lg">
                {chamado.sac_parecer_final}
              </p>
            </div>
          )}

          {anexosChamado.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-gray-500 mb-2">Anexos do Chamado</p>
              <div className="space-y-2">
                {anexosChamado.map((anexo) => (
                  <div
                    key={anexo.anx_codigo}
                    className="flex items-center gap-2 text-sm"
                  >
                    <FileText className="w-4 h-4 text-n-text-neutral" />
                    <a
                      href={`${import.meta.env.VITE_API_URL}/${
                        anexo.anx_arquivo
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-n-green hover:text-n-green/80"
                    >
                      {getFileName(anexo.anx_arquivo)}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 bg-transparent shadow-none">
        <CardHeader>
          <CardTitle className="text-lg text-n-green">
            Histórico de Mensagens
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-n-green" />
              <span className="ml-2 text-gray-600">
                Carregando mensagens...
              </span>
            </div>
          ) : mensagens.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              Nenhuma mensagem registrada.
            </p>
          ) : (
            <div>
              {mensagens.map((mensagem, index) => {
                const isCliente = mensagem.smg_cliente_enviou === "1";

                return (
                  <div key={mensagem.smg_codigo} className="relative pt-1">
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div className="relative z-10 max-w-[30px] min-w-[30px] min-h-[30px] max-h-[30px] rounded-full bg-zinc-200 border-2 border-zinc-300 flex items-center justify-center">
                          <Check
                            className={`h-4 w-4 ${
                              isCliente ? "text-blue-800" : "text-n-green"
                            }`}
                          />
                        </div>
                        {index !== mensagens.length - 1 && (
                          <div className="w-0.5 h-full bg-transparent border-l-2 border-dashed border-gray-300 mt-1" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0 pb-6 border-t pt-2 mt-3">
                        <div className="flex flex-row justify-between items-start mb-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <div
                              className={`inline-flex items-center gap-4 rounded-full p-[4px] ${
                                isCliente ? "bg-n-beige" : "bg-n-green-s-light"
                              }`}
                            >
                              <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                  isCliente ? "bg-n-green" : "bg-n-green"
                                }`}
                              >
                                {isCliente ? (
                                  <User className="h-3.5 w-3.5 text-white" />
                                ) : (
                                  <img
                                    src="/images/favicon.png"
                                    alt="Nacional GO"
                                    className="h-4 w-4"
                                  />
                                )}
                              </div>
                              <span
                                className={`font-semibold text-sm pr-4 ${
                                  isCliente
                                    ? "text-n-text-green"
                                    : "text-n-text-green"
                                }`}
                              >
                                {isCliente ? "Você" : "Nacional"}
                              </span>
                            </div>

                            <span className="text-xs text-gray-500">
                              {mensagem.smg_titulo ||
                                (isCliente
                                  ? mensagem.smg_texto.substring(0, 30) + "..."
                                  : "Resposta da empresa")}
                            </span>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className="text-xs text-gray-400">
                              Data:{" "}
                              {format(
                                new Date(mensagem.smg_data_envio),
                                "dd/MM/yyyy",
                                { locale: ptBR },
                              )}
                            </span>
                          </div>
                        </div>

                        <p className="text-sm text-gray-700 mb-2 whitespace-pre-wrap">
                          {mensagem.smg_texto}
                        </p>

                        {anexosMensagens[mensagem.smg_codigo]?.length > 0 && (
                          <div className="space-y-2 mb-2">
                            {anexosMensagens[mensagem.smg_codigo].map(
                              (anexo) => (
                                <div
                                  key={anexo.anx_codigo}
                                  className="flex items-center gap-2 text-sm"
                                >
                                  <FileText className="w-4 h-4 text-n-text-neutral flex-shrink-0" />
                                  <a
                                    href={`${import.meta.env.VITE_API_URL}/${
                                      anexo.anx_arquivo
                                    }`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline text-n-green hover:text-n-green/80 truncate"
                                  >
                                    {getFileName(anexo.anx_arquivo)}
                                  </a>
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
